import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const ExpenseAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('Expense Analytics');
    fetchAnalytics();
    fetchPaymentAnalytics();
    fetchRecipients();
  }, [setPageTitle]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get('/api/expense/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Error fetching analytics');
    }
  };

  const fetchPaymentAnalytics = async () => {
    try {
      const response = await axios.get('/api/payment/analytics');
      setPaymentAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching payment analytics');
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await axios.get('/api/expense-recipient');
      setRecipients(response.data);
    } catch (error) {
      console.error('Error fetching recipients');
    }
  };

  if (!hasPermission('expense', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  if (!analytics || !paymentAnalytics || !recipients) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading analytics...</p>
      </div>
    );
  }

  const totalDue = recipients.reduce((sum, r) => sum + (r.totalDueAmount || 0), 0);
  const totalPaid = paymentAnalytics.totalPaid || 0;

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card" style={{background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'}}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-2">📈 Expense Analytics</h4>
                  <p className="mb-0 opacity-75">Complete overview of expense flow, approvals, and payments</p>
                  <small className="opacity-75">Track money movement from approval to payment completion</small>
                </div>
                <div className="text-end">
                  <div className="h5 mb-0">${(analytics.totalApproved + analytics.totalPending + analytics.totalDraft).toFixed(2)}</div>
                  <small className="opacity-75">Total Expense Volume</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body text-center">
              <div className="text-success mb-2" style={{fontSize: '2rem'}}>✓</div>
              <h5 className="card-title text-success">Approved</h5>
              <h3 className="mb-1">${analytics.totalApproved.toFixed(2)}</h3>
              <small className="text-muted">Ready for payment</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-info">
            <div className="card-body text-center">
              <div className="text-info mb-2" style={{fontSize: '2rem'}}>💰</div>
              <h5 className="card-title text-info">Amount Due</h5>
              <h3 className="mb-1">${totalDue.toFixed(2)}</h3>
              <small className="text-muted">Awaiting payment</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-primary">
            <div className="card-body text-center">
              <div className="text-primary mb-2" style={{fontSize: '2rem'}}>💳</div>
              <h5 className="card-title text-primary">Total Paid</h5>
              <h3 className="mb-1">${totalPaid.toFixed(2)}</h3>
              <small className="text-muted">Completed payments</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body text-center">
              <div className="text-warning mb-2" style={{fontSize: '2rem'}}>⏳</div>
              <h5 className="card-title text-warning">Pending</h5>
              <h3 className="mb-1">${analytics.totalPending.toFixed(2)}</h3>
              <small className="text-muted">Awaiting approval</small>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Flow Overview */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">🔄 Payment Flow Overview</h6>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-2">
                  <div className="border rounded p-3 bg-light">
                    <div className="h4 text-secondary">${analytics.totalDraft.toFixed(2)}</div>
                    <small className="text-muted">Draft</small>
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-center justify-content-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
                <div className="col-md-2">
                  <div className="border rounded p-3 bg-warning bg-opacity-10">
                    <div className="h4 text-warning">${analytics.totalPending.toFixed(2)}</div>
                    <small className="text-muted">Pending</small>
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-center justify-content-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
                <div className="col-md-2">
                  <div className="border rounded p-3 bg-success bg-opacity-10">
                    <div className="h4 text-success">${analytics.totalApproved.toFixed(2)}</div>
                    <small className="text-muted">Approved</small>
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-center justify-content-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
                <div className="col-md-2">
                  <div className="border rounded p-3 bg-info bg-opacity-10">
                    <div className="h4 text-info">${totalDue.toFixed(2)}</div>
                    <small className="text-muted">Due</small>
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-center justify-content-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
              </div>
              <div className="text-center mt-3">
                <div className="border rounded p-3 bg-primary bg-opacity-10 d-inline-block">
                  <div className="h4 text-primary">${totalPaid.toFixed(2)}</div>
                  <small className="text-muted">Paid</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recipient Analysis */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">📄 Recipient Analysis</h6>
                <small className="text-muted">{analytics.byRecipient.length} recipients with expenses</small>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Recipient</th>
                    <th>Type</th>
                    <th>Approved</th>
                    <th>Due Amount</th>
                    <th>Paid</th>
                    <th>Pending</th>
                    <th>Draft</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.byRecipient.map(item => {
                    const recipientData = recipients.find(r => r._id === item._id) || {};
                    const dueAmount = recipientData.totalDueAmount || 0;
                    const paidAmount = recipientData.totalPaidAmount || 0;
                    return (
                      <tr key={item._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle me-2" style={{ width: '32px', height: '32px', fontSize: '0.8rem' }}>
                              {item.recipient.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-semibold">{item.recipient.name}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${
                            item.recipient.type === 'employee' ? 'bg-primary' :
                            item.recipient.type === 'vendor' ? 'bg-success' :
                            item.recipient.type === 'contractor' ? 'bg-info' :
                            item.recipient.type === 'partner' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {item.recipient.type.charAt(0).toUpperCase() + item.recipient.type.slice(1)}
                          </span>
                        </td>
                        <td className="text-success fw-semibold">${item.totalApproved.toFixed(2)}</td>
                        <td className={`fw-bold ${dueAmount > 0 ? 'text-danger' : 'text-muted'}`}>
                          ${dueAmount.toFixed(2)}
                        </td>
                        <td className="text-info fw-semibold">${paidAmount.toFixed(2)}</td>
                        <td className="text-warning">${item.totalPending.toFixed(2)}</td>
                        <td className="text-muted">${item.totalDraft.toFixed(2)}</td>
                        <td>
                          {dueAmount > 0 ? (
                            <span className="badge bg-warning">Payment Due</span>
                          ) : item.totalPending > 0 ? (
                            <span className="badge bg-info">Pending Approval</span>
                          ) : item.totalApproved > 0 ? (
                            <span className="badge bg-success">Paid Up</span>
                          ) : (
                            <span className="badge bg-light text-dark">Draft Only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Insights */}
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">📊 Expense Status Distribution</h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-success">Approved & Ready</span>
                  <span className="fw-bold">${analytics.totalApproved.toFixed(2)}</span>
                </div>
                <div className="progress mb-2" style={{height: '8px'}}>
                  <div 
                    className="progress-bar bg-success" 
                    style={{
                      width: `${(analytics.totalApproved / (analytics.totalApproved + analytics.totalPending + analytics.totalDraft)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-warning">Pending Approval</span>
                  <span className="fw-bold">${analytics.totalPending.toFixed(2)}</span>
                </div>
                <div className="progress mb-2" style={{height: '8px'}}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{
                      width: `${(analytics.totalPending / (analytics.totalApproved + analytics.totalPending + analytics.totalDraft)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-secondary">Draft</span>
                  <span className="fw-bold">${analytics.totalDraft.toFixed(2)}</span>
                </div>
                <div className="progress" style={{height: '8px'}}>
                  <div 
                    className="progress-bar bg-secondary" 
                    style={{
                      width: `${(analytics.totalDraft / (analytics.totalApproved + analytics.totalPending + analytics.totalDraft)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">💡 Key Insights</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="d-flex align-items-center p-2 bg-success bg-opacity-10 rounded">
                    <i className="fas fa-check-circle text-success me-3"></i>
                    <div>
                      <div className="fw-semibold">${analytics.totalApproved.toFixed(2)} Approved</div>
                      <small className="text-muted">Ready for payment processing</small>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center p-2 bg-info bg-opacity-10 rounded">
                    <i className="fas fa-dollar-sign text-info me-3"></i>
                    <div>
                      <div className="fw-semibold">${totalDue.toFixed(2)} Due</div>
                      <small className="text-muted">Awaiting payment to recipients</small>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center p-2 bg-primary bg-opacity-10 rounded">
                    <i className="fas fa-credit-card text-primary me-3"></i>
                    <div>
                      <div className="fw-semibold">${totalPaid.toFixed(2)} Paid</div>
                      <small className="text-muted">Successfully completed payments</small>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center p-2 bg-warning bg-opacity-10 rounded">
                    <i className="fas fa-users text-warning me-3"></i>
                    <div>
                      <div className="fw-semibold">{recipients.filter(r => (r.totalDueAmount || 0) > 0).length} Recipients</div>
                      <small className="text-muted">Need payment processing</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAnalytics;